"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, ShieldCheck, Wrench, FolderKanban } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface ServiceCardItem {
  title: string;
  category: string;
  description: string;
  to: string;
  iconName: string;
  photoName: string;
  fallbackSrc: string;
  fallbackAlt: string;
}
const servicesData: ServiceCardItem[] = [{
  title: "Commercial Scaffolding",
  category: "Main Contractors & Developments",
  description: "Full-scale access systems, temporary roofs, and perimeter scaffolds for new builds, refurbishments, and industrial contracts.",
  to: "/CommercialScaffoldingPage",
  iconName: "commercial",
  photoName: "Industrial Related Commercial Image",
  fallbackSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_site.webp",
  fallbackAlt: "Commercial scaffolding erected on a multi-storey development"
}, {
  title: "Specialist Access Scaffolding",
  category: "Complex & Bespoke Structures",
  description: "Engineered cantilever scaffolds, suspended platforms, and restricted-access solutions calculated to exact loading requirements.",
  to: "/SpecialistAccessScaffoldingPage",
  iconName: "specialist",
  photoName: "Industrial Related Specialist Access Image",
  fallbackSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/specialist_access_scaffolding.png",
  fallbackAlt: "Bespoke engineered scaffolding access structure"
}, {
  title: "Property & Facilities Management",
  category: "Planned & Reactive Maintenance",
  description: "Responsive scaffolding support for building surveyors, managing agents, and estate teams needing safe maintenance access.",
  to: "/PropertyFacilitiesManagementPage",
  iconName: "facilities",
  photoName: "Industrial Related Facilities Image",
  fallbackSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/facilities_management_access.png",
  fallbackAlt: "Maintenance scaffolding for commercial property management"
}, {
  title: "Projects & Case Studies",
  category: "Portfolio & Recent Works",
  description: "Browse completed scaffolding projects across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.",
  to: "/ProjectsCaseStudiesPage",
  iconName: "projects",
  photoName: "Industrial Related Projects Image",
  fallbackSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/bmf_projects_overview.png",
  fallbackAlt: "BMF Scaffolding completed client project on site"
}];
function getServiceIcon(iconName: string) {
  switch (iconName) {
    case "commercial":
      return <Building2 className="size-5 text-primary" />;
    case "specialist":
      return <Wrench className="size-5 text-primary" />;
    case "facilities":
      return <ShieldCheck className="size-5 text-primary" />;
    case "projects":
      return <FolderKanban className="size-5 text-primary" />;
    default:
      return <Building2 className="size-5 text-primary" />;
  }
}
function RelatedServicesConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const resolvePhoto = (photoName: string, fallbackSrc: string, fallbackAlt: string) => {
    const match = posts.find(p => p.customFields?.photo_name?.toLowerCase() === photoName.toLowerCase() || p.title?.toLowerCase() === photoName.toLowerCase());
    if (match) {
      const src = match.customFields?.image || match.featuredImage || fallbackSrc;
      const alt = match.customFields?.alt_text || fallbackAlt;
      const isCustom = Boolean(match.customFields?.image || match.featuredImage);
      return {
        src,
        srcset: isCustom ? "" : `${src}-wvc-srcset`,
        sizes: isCustom ? "" : `${src}-wvc-sizes`,
        alt
      };
    }
    return {
      src: fallbackSrc,
      srcset: `${fallbackSrc}-wvc-srcset`,
      sizes: `${fallbackSrc}-wvc-sizes`,
      alt: fallbackAlt
    };
  };
  return <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
    once: true,
    amount: 0.15
  }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {servicesData.map((service, index) => {
      const image = resolvePhoto(service.photoName, service.fallbackSrc, service.fallbackAlt);
      return <motion.div key={service.title} data-index={index} variants={cardVariants} className="flex h-full">
            <Card className="flex flex-col justify-between h-full w-full overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200 hover:border-primary/40 group">
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />}
                <div className="absolute top-3 left-3 bg-card/90 backdrop-blur-xs p-2 rounded-md shadow-xs border border-border/60">
                  {getServiceIcon(service.iconName)}
                </div>
              </div>

              <CardHeader className="pt-5 pb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {service.category}
                </span>
                <CardTitle className="text-xl font-bold text-card-foreground group-hover:text-primary transition-colors font-default mt-1">
                  {service.title}
                </CardTitle>
              </CardHeader>

              <CardContent className="py-2 grow">
                <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                  {service.description}
                </CardDescription>
              </CardContent>

              <CardFooter className="pt-4 pb-6">
                <Button asChild variant="ghost" size="sm" className="w-full justify-between px-0 font-semibold text-primary hover:bg-transparent group/btn">
                  <Link to={service.to}>
                    <span>Learn more</span>
                    <ArrowRight className="size-4 transform transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>;
    })}
    </motion.div>;
}
export default function RelatedServices() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 50
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="relatedservicesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-3 border-border text-foreground font-medium">
              Comprehensive Access Solutions
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Explore related scaffolding services
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-default">
              Access solutions engineered for site safety, regulatory compliance, and dependable project schedules across the South East.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild size="lg" className="shadow-sm">
              <Link to="/ContactRequestQuotePage">
                Request a Quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Services Grid with PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "site_photo",
        per_page: 50,
        status: ["publish"]
      }}>
          <RelatedServicesConsumer />
        </PostsProvider>

        {/* Accreditations & Bottom Support Bar */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">
            Fully certified by <strong className="text-foreground font-semibold">NASC</strong> and <strong className="text-foreground font-semibold">CHAS</strong>.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Need advice on structural requirements?</span>
            <Button asChild variant="outline" size="sm">
              <Link to="/ContactRequestQuotePage">
                Speak with our team
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}